import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Shield, Clock, Wrench, FileCheck, Home, Heart, ArrowRight } from 'lucide-react';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const SoCalRebuilds = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';

  const steps = [
    { icon: Phone, title: isZh ? '免费咨询' : 'Free Consultation', desc: isZh ? '联系我们，讨论您的重建需求和选择方案。' : 'Contact us to discuss your rebuilding needs and available options.' },
    { icon: FileCheck, title: isZh ? '评估与规划' : 'Assessment & Planning', desc: isZh ? '我们评估您的物业，制定详细的重建计划和时间表。' : 'We assess your property and create a detailed rebuild plan and timeline.' },
    { icon: Shield, title: isZh ? '许可与审批' : 'Permitting', desc: isZh ? '利用我们的预审批方案加速许可流程。' : 'Leverage our pre-approved plans to expedite the permitting process.' },
    { icon: Wrench, title: isZh ? '快速施工' : 'Rapid Construction', desc: isZh ? '利用高效建造系统，4-6个月内完成施工。' : 'Using our efficient building system, construction completes in 4-6 months.' },
    { icon: Home, title: isZh ? '交付入住' : 'Move-In Ready', desc: isZh ? '全面质检后交付，让您尽快重返家园。' : 'After comprehensive inspection, your home is delivered move-in ready.' }
  ];

  const features = [
    { title: isZh ? '加速许可流程' : 'Expedited Permitting', desc: isZh ? '预审批方案大幅缩短许可审批时间' : 'Pre-approved plans significantly reduce permit approval time' },
    { title: isZh ? '保险理赔协助' : 'Insurance Claim Assistance', desc: isZh ? '协助您处理保险理赔流程' : 'We help navigate the insurance claim process' },
    { title: isZh ? '快速建造' : 'Rapid Build Timeline', desc: isZh ? '4-6个月完成，远快于传统施工' : '4-6 month completion, much faster than traditional construction' },
    { title: isZh ? '透明定价' : 'Transparent Pricing', desc: isZh ? '无隐藏费用，预先明确所有成本' : 'No hidden costs, all expenses outlined upfront' },
    { title: isZh ? '全包服务' : 'Turnkey Service', desc: isZh ? '从设计到交付的一站式服务' : 'Complete service from design to delivery' },
    { title: isZh ? '品质保证' : 'Quality Guarantee', desc: isZh ? '使用优质建材，提供全面质保' : 'Premium materials with comprehensive warranty' }
  ];

  return (
    <div className="min-h-screen bg-white ">
      <SEO
        title={isZh ? '南加州火灾重建 ADU | 4-6 个月快速重建家园 | ALIGCUS 阿里建筑' : 'SoCal Wildfire Home Rebuild | 4-6 Month Fast Reconstruction | ALIGCUS Construction'}
        description={isZh ? 'ALIGCUS 为南加州火灾受灾家庭提供快速、优质、透明的重建服务。预审批方案加速许可，4-6 个月完工，保险理赔协助。免费咨询 (626) 803-8000。' : 'ALIGCUS provides fast, quality, transparent rebuild services for SoCal wildfire-affected families. Pre-approved plans expedite permitting, 4-6 month completion, insurance claim assistance. Free consultation (626) 803-8000.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/socal-rebuilds' : 'https://www.aligcus.com/socal-rebuilds'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/socal-rebuilds' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/socal-rebuilds' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '南加重建' : 'SoCal Rebuilds', url: isZh ? 'https://www.aligcus.com/zh/socal-rebuilds' : 'https://www.aligcus.com/socal-rebuilds' },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/80 to-gray-900/90" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-red-600 rounded-full text-sm font-semibold mb-6">
            {isZh ? '南加州火灾重建支援' : 'SoCal Fire Recovery Support'}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">{isZh ? '重建南加州' : 'Rebuilding SoCal'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            {isZh ? '我们与受灾社区站在一起。ALIGCUS 为火灾受灾家庭提供快速、优质、透明的重建服务，帮助您尽快重返家园。' : 'We stand with affected communities. ALIGCUS provides fast, quality, and transparent rebuilding services for fire-affected families, helping you return home as quickly as possible.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-6 text-lg">
                {isZh ? '免费重建咨询' : 'Free Rebuild Consultation'}
              </Button>
            </Link>
            <a href="tel:626-803-8000">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" /> (626) 803-8000
              </Button>
            </a>
          </div>
          <div className="flex gap-3 justify-center mt-4">
            {[{l:'SoCal',n:'(626) 803-8000',t:'6268038000'},{l:'NorCal',n:'(408) 900-6688',t:'4089006688'},{l:'San Diego',n:'858-910-0000',t:'8589100000'}].map(p=>(
              <a key={p.l} href={`tel:${p.t}`} className="flex flex-col items-center gap-1 px-4 py-2 border border-white/30 rounded-xl hover:bg-white/10 transition-all">
                <span className="text-xs font-bold text-white/70">{p.l}</span>
                <span className="text-sm font-bold text-white">{p.n}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Message */}
      <section className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Heart className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{isZh ? '我们与您同在' : 'We Are Here For You'}</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            {isZh ? '南加州的火灾给许多家庭带来了巨大的损失。作为扎根本地的建筑公司，ALIGCUS 深感责任重大。我们承诺以最快的速度、最优的品质和最透明的价格，帮助每一个受灾家庭重建美好家园。' : 'The Southern California fires have brought tremendous loss to many families. As a locally rooted construction company, ALIGCUS feels a deep responsibility. We are committed to helping every affected family rebuild their homes with the fastest speed, highest quality, and most transparent pricing.'}
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">{isZh ? '重建流程' : 'Rebuild Process'}</h2>
          <div className="grid md:grid-cols-5 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-sm font-bold text-red-600 mb-1">Step {i + 1}</div>
                <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">{isZh ? '为什么选择 ALIGCUS' : 'Why Choose ALIGCUS'}</h2>
          <p className="text-xl text-gray-600 text-center mb-16">{isZh ? '专业、高效、值得信赖的重建伙伴' : 'Your professional, efficient, and trusted rebuild partner'}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="shadow-lg hover:shadow-xl transition-all border-0">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-600">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">{isZh ? '立即开始您的重建之路' : 'Start Your Rebuild Journey Today'}</h2>
          <p className="text-gray-400 mb-8">{isZh ? '我们的团队随时准备为您提供帮助' : 'Our team is ready to help you every step of the way'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/single-family-homes">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-6 text-lg">
                {isZh ? '了解更多' : 'Learn More'} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoCalRebuilds;
